// Device Kits page — list, assign, and return medical devices/instruments.

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { KitRow, type DeviceKit } from "../components/KitRow.js";
import { AssignKitModal } from "../components/AssignKitModal.js";
import { KitNotesModal } from "../components/KitNotesModal.js";

// /device-kits returns { data, total, page, limit } — use the `select`
// transformer to unwrap so the component receives DeviceKit[] directly.
// Without this, kits.map/.filter would crash at runtime even though
// useQuery<DeviceKit[]> compiled cleanly.
type DeviceKitsEnvelope = { data: DeviceKit[]; total: number; page: number; limit: number };

function useKits(status?: string): ReturnType<typeof useQuery<DeviceKitsEnvelope, Error, DeviceKit[]>> {
  return useQuery<DeviceKitsEnvelope, Error, DeviceKit[]>({
    queryKey: ["device-kits", status],
    queryFn: () => apiFetch<DeviceKitsEnvelope>(`/device-kits${status ? `?status=${status}` : ""}`),
    select: (res) => res.data,
  });
}

export function DeviceKitsPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [assigningId, setAssigningId]   = useState<string | null>(null);
  const [editingKit,  setEditingKit]    = useState<DeviceKit | null>(null);
  const qc = useQueryClient();
  const { data: kits, isLoading, isError } = useKits(statusFilter || undefined);

  const returnKit = useMutation({
    mutationFn: (id: string) => apiFetch(`/device-kits/${id}/return`, { method: "POST", body: "{}" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["device-kits"] }),
  });

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-slate-900">Device Kits</h1>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
            <option value="">All statuses</option>
            <option value="available">Available</option>
            <option value="assigned">Assigned</option>
            <option value="maintenance">Maintenance</option>
            <option value="retired">Retired</option>
          </select>
        </div>

        {isLoading && <p className="py-8 text-center text-sm text-slate-400">Loading…</p>}

        {isError && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-xl px-6 py-4">
            <p className="text-sm text-red-700 font-medium">Failed to load device kits.</p>
          </div>
        )}

        {!isLoading && !isError && kits && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Serial</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cal. Due</th>
                  <th className="py-2.5 px-4" />
                </tr>
              </thead>
              <tbody>
                {kits.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-400">No device kits found.</td></tr>
                )}
                {kits.map((kit) => (
                  <KitRow key={kit._id} kit={kit}
                    onAssign={setAssigningId}
                    onReturn={(id) => returnKit.mutate(id)}
                    onEditNotes={setEditingKit} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {assigningId && (
          <AssignKitModal kitId={assigningId}
            onClose={() => setAssigningId(null)}
            onSuccess={() => setAssigningId(null)} />
        )}
        {editingKit && (
          <KitNotesModal kit={editingKit}
            onClose={() => setEditingKit(null)}
            onSaved={() => setEditingKit(null)} />
        )}
      </div>
    </Shell>
  );
}
